import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useConfirmarProgramaEmLote } from "@/hooks/use-manejo";
import type { PropostaProgramaEmLote, PropostaProgramaSetor, Pergunta } from "@shared/manejo-proposta";
import type { MnjProgramaComItens } from "@/hooks/use-manejo";
import { CheckCircle2, HelpCircle } from "lucide-react";

// Card de confirmação da capacidade manejo.definir_programa_em_lote (Sprint 3 §3.1).
// Mesmo padrão visual/de transação atômica do PropostaCard (setores separados por
// `.map` dentro de UM card, um botão só) — mas é um tipo de proposta diferente
// (kgPorHa de meta, não kgTotal de execução), por isso um componente próprio.
// `propor()` da capacidade devolve uma LISTA destas — normalmente 1, e 2+ quando
// há exceção nomeada (ex. C1, produtos diferentes): cada uma é renderizada como um
// PropostaProgramaCard independente, com sua própria pendência e confirmação.

function formatarPergunta(p: Pergunta): string {
  const ctx = p.contexto ?? {};
  switch (p.tipo) {
    case "setor_nao_encontrado":
      return `Não encontrei o setor "${p.setorTexto}" entre os setores fertirrigáveis cadastrados.`;
    case "produto_nao_encontrado":
      return `Não reconheço "${p.produtoTexto}". A qual produto cadastrado corresponde?`;
    case "programa_ativo_existente":
      return `"${p.setorTexto}" já tem um programa ativo ("${ctx.programaAtivoNome}"). Sobrescrever ou criar um novo período?`;
    case "dose_nao_informada":
      return `"${p.produtoTexto}" veio sem dose no texto. Qual a dose (kg/ha)?`;
    default:
      return "Confirme este item.";
  }
}

interface PropostaProgramaCardProps {
  proposta: PropostaProgramaEmLote;
  onConfirmado?: (resultado: { programas: MnjProgramaComItens[] }) => void;
}

export function PropostaProgramaCard({ proposta, onConfirmado }: PropostaProgramaCardProps) {
  const { toast } = useToast();
  const [propostaLocal, setPropostaLocal] = useState(proposta);
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [confirmado, setConfirmado] = useState(false);

  const confirmarLote = useConfirmarProgramaEmLote();

  const responderPergunta = (pergunta: Pergunta, valor: string) => {
    if (pergunta.tipo === "dose_nao_informada") {
      const numero = Number(valor.replace(",", "."));
      if (!valor.trim() || isNaN(numero) || numero <= 0) {
        // Vazio/inválido: nunca marca como respondida — confirmar continua
        // bloqueado, nunca grava produto sem quantidade.
        setRespostas(r => {
          const { [pergunta.id]: _remover, ...resto } = r;
          return resto;
        });
        return;
      }
      setRespostas(r => ({ ...r, [pergunta.id]: valor }));
      setPropostaLocal(p => ({
        ...p,
        itens: p.itens.map(it => it.produtoTexto === pergunta.produtoTexto ? { ...it, kgPorHa: numero } : it),
      }));
      return;
    }

    setRespostas(r => ({ ...r, [pergunta.id]: valor }));
    if (pergunta.tipo === "setor_nao_encontrado") {
      setPropostaLocal(p => ({
        ...p,
        setores: p.setores.map(s => s.setorTexto === pergunta.setorTexto ? { ...s, setorId: valor } : s),
      }));
    }
    if (pergunta.tipo === "produto_nao_encontrado") {
      setPropostaLocal(p => ({
        ...p,
        itens: p.itens.map(it => it.produtoTexto === pergunta.produtoTexto ? { ...it, mnjProdutoId: valor } : it),
      }));
    }
    // programa_ativo_existente: só grava em `respostas` — usada no payload de
    // confirmação como `modo`, nunca altera a proposta em si (não há o que mudar
    // na tela, o setor já está resolvido; a escolha decide criar × sobrescrever).
  };

  const perguntasPendentes = useMemo(
    () => propostaLocal.perguntas.filter(p => !(p.id in respostas)),
    [propostaLocal, respostas],
  );

  const podeConfirmar =
    !confirmado &&
    perguntasPendentes.length === 0 &&
    propostaLocal.setores.every((s: PropostaProgramaSetor) => s.setorId) &&
    propostaLocal.itens.every(it => it.mnjProdutoId && it.kgPorHa !== null && it.kgPorHa > 0);

  const handleConfirmar = async () => {
    if (!podeConfirmar) return;
    const payload = {
      nomePrograma: propostaLocal.nomePrograma,
      periodoInicio: propostaLocal.periodoInicio,
      periodoFim: propostaLocal.periodoFim,
      // kgPorHa vai como string no wire — mesma convenção do resto do CRUD de
      // programa (ProgramaItemInput/programa.tsx); o schema (mnjProgramaItemInputSchema)
      // aceita number|string mas o TIPO inferido do commit já é pós-transform (string).
      itens: propostaLocal.itens.map(it => ({ mnjProdutoId: it.mnjProdutoId!, kgPorHa: String(it.kgPorHa!) })),
      setores: propostaLocal.setores.map(s => {
        const perguntaModo = propostaLocal.perguntas.find(p => p.tipo === "programa_ativo_existente" && p.setorTexto === s.setorTexto);
        const modo = perguntaModo ? (respostas[perguntaModo.id] as "sobrescrever" | "novo_periodo" | undefined) : undefined;
        return { setorId: s.setorId!, modo };
      }),
    };
    try {
      const resultado = await confirmarLote.mutateAsync(payload);
      toast({ title: `${resultado.programas.length} programa(s) confirmado(s)!` });
      setConfirmado(true);
      onConfirmado?.(resultado);
    } catch (e: any) {
      toast({ title: "Erro ao confirmar", description: e.message, variant: "destructive" });
    }
  };

  if (confirmado) {
    return (
      <div className="rounded-lg border bg-sucesso-suave border-sucesso/30 p-3 text-sm text-sucesso flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        {propostaLocal.nomePrograma} confirmado para {propostaLocal.setores.length} setor(es).
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="secondary">{propostaLocal.nomePrograma}</Badge>
        <span className="text-xs text-muted-foreground">{propostaLocal.setores.length} setor(es)</span>
      </div>

      {propostaLocal.perguntas.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <HelpCircle className="h-4 w-4 text-atencao" /> Perguntas
          </p>
          {propostaLocal.perguntas.map(p => (
            <div key={p.id} className="rounded-md border border-atencao/30 bg-atencao-suave p-3 space-y-2">
              <p className="text-sm">{formatarPergunta(p)}</p>
              {p.tipo === "dose_nao_informada" ? (
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="ex.: 1,50"
                  className="w-32"
                  defaultValue={respostas[p.id] ?? ""}
                  onChange={e => responderPergunta(p, e.target.value)}
                />
              ) : p.opcoes ? (
                <div className="flex flex-wrap gap-1.5">
                  {p.opcoes.map(o => (
                    <Button
                      key={o.valor}
                      size="sm"
                      variant={respostas[p.id] === o.valor ? "default" : "outline"}
                      onClick={() => responderPergunta(p, o.valor)}
                    >
                      {o.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Itens — doses (kg/ha) compartilhadas por TODOS os setores abaixo */}
      <div className="space-y-1">
        <p className="text-sm font-semibold">Produtos (kg/ha)</p>
        {propostaLocal.itens.map((it, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <span>
              {it.produtoTexto}
              {!it.mnjProdutoId && <Badge variant="destructive" className="ml-2 text-[10px]">?</Badge>}
            </span>
            <span className="tabular-nums">{it.kgPorHa !== null ? `${it.kgPorHa} kg/ha` : "—"}</span>
          </div>
        ))}
      </div>

      {/* Setores — separação visual, mesma proposta, mesma transação atômica */}
      <div className="space-y-2">
        <p className="text-sm font-semibold">Setores ({propostaLocal.setores.length})</p>
        <div className="flex flex-wrap gap-1.5">
          {propostaLocal.setores.map((s, idx) => (
            <div key={idx} className="rounded-md border p-2 px-3 text-sm flex items-center gap-1.5">
              {s.setorTexto}
              {!s.setorId && <Badge variant="destructive" className="text-[10px]">não resolvido</Badge>}
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground italic">números calculados pelo módulo, não pelo modelo</p>

      <div className="flex justify-end pt-1">
        <Button onClick={handleConfirmar} disabled={!podeConfirmar || confirmarLote.isPending} size="lg">
          {confirmarLote.isPending ? "Confirmando..." : "Confirmar programa"}
        </Button>
      </div>
    </div>
  );
}
